/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: BI Cloud (C) Copyright IBM Corp. 2015, 2018
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 */
define([
	'jquery',
	'../../../lib/@waca/core-client/js/core-client/ui/core/Class',
	'../../../filters/Filters',
	'../../../filters/FilterEntry',
	'underscore' ],
function($, Class, FilterCollection, FilterEntry, _) {
	'use strict';


	/**
	 * The QueryFilterSpec object is responsible for converting filter collections for presentation
	 * to the query engine (as a query spec).
	 * Collections may need to be pre-processed prior to adding
	 * (for example, dataPoint filters need to be merged with edge filters on the same dimension as 'or' terms rather than as separate filters).
	 * NOTE: Merged filters are ONLY FOR QUERY PURPOSES.  The filter collections are not changed
	 * (other than a temporary instance used for query generation).
	 */
	// Declare the class
	var QueryFilterSpec = null;
	QueryFilterSpec = Class.extend({

	/**
		 * Initialize the queryFilters object
		 * @param visibleItemsMapParam - A map of the visible itemId's to an array of dataItemAPIs, layers/views they're in etc.
		 */
		init: function(visibleItemsMapParam) {
			var visibleItemsMap = visibleItemsMapParam || [];
			this._queryFilterSpec = [];
			this._dataItemsMap = {};
			_.each(_.values(visibleItemsMap), function(visibleItemInfoArray) {
				_.each(visibleItemInfoArray, function(visibleItemInfo) {
					var dataItemAPI = visibleItemInfo.dataItemAPI;
					if (dataItemAPI.isBinned()) {
						this._dataItemsMap[dataItemAPI.getItemId()] = visibleItemInfoArray;
					} else {
						this._dataItemsMap[FilterEntry.prototype.determineIdValue(dataItemAPI.getItemId(), dataItemAPI.getAggregationType())] = visibleItemInfoArray;
					}
				}.bind(this));
			}.bind(this));
		},

		/**
		 * incorporate a filter collection into the query filter spec
		 * AFTER merging include type datapoint with edge filters on the same items.
		 *
		 * @param filterCollection - the collection to add to the spec
		 * @param options - options to the add (usu. exceptions which are defined for the main filter collection).
		 */
		addFiltersToSpec: function(filterCollection, options) {
			options=options || {};
			this.addColumnFiltersToSpec(this._mergeEdgeAndPointFiltersOnSameColumns(filterCollection, options), options);
		},

		/**
		 * incorporate the filters from a filter collection to the query filter spec.
		 * Note: this method should only be called if it is known that a filterCollection has only column-type filters (ie: no datapoint filters)
		 *		(or if the merge step has already been completed).
		 * @param filterCollection - the collection to add to the spec
		 * @param options - options to the add (usu. exceptions which are defined for the main filter collection).
		 */
		addColumnFiltersToSpec: function(filterCollection, options) {
			this._queryFilterSpec = this._queryFilterSpec.concat(this._toSpec(filterCollection, options));
		},

		/**
		 * @returns the query spec for addition to the filters element of the query.
		 */
		getFilterSpec: function() {
			return this._queryFilterSpec;
		},

		/**
		 * @returns true if there is a non-empty filter spec to add to the query.
		 */
		hasFilterSpec: function() {
			return (this._queryFilterSpec.length > 0);
		},

		/**
		 * Returns true if the filter entry has no meaningful content or values.
		 * @param {object} filterEntry
		 * @returns {boolean}
		 */
		_isFilterEntryEmpty: function(filterEntry) {
			return !filterEntry.valueDataItem &&
				!filterEntry.conditions &&
				filterEntry.values && filterEntry.values.isEmpty() &&
				filterEntry.operator !== 'notin';
		},

		/**
		 * @param filterEntry - the filterEntry being processed
		 * @param exceptions - a map of the filter exceptions
		 * @param applyToCompoundFilters - true if the exceptions should be applied to compound filters.
		 * @param options - Object of options passed
		 * @returns true if this filterEntry should be excepted from the filters included in the query.
		 * (this usually corresponds to visible fields (or data points).
		 */
		_isFilterException: function(filterEntry, exceptions, applyToCompoundFilters, options) {
			if (this._isFilterEntryEmpty(filterEntry)) {
				return true;
			} else if (filterEntry.isOrdinalFilter()) {
				var projectedInAnyLayer = this._dataItemsMap[filterEntry.id];
				if (!projectedInAnyLayer) {
					//This is column type range filter that's not projected in ANY layer.
					//...if not including non-projected range filters, this filter is a filter exception.
					if (options.excludeNonProjectedRangeFilters) {
						return true;
					}
				} else {
					//TODO: change layer to view.
					var projectedInThisLayer = _.find(projectedInAnyLayer, function(projectedItem) {
						return (projectedItem.layerId === options.layerId);
					});
					if (!projectedInThisLayer) {
						//This is a dataitem type range filter but the item isn't projected in this layer.
						//Don't include it in the filters for this layer.
						return true;
					}
				}
			}

			// exceptions is built from _prepareFilterExceptionsMap in which exceptions map is built from filterExceptions or edgeFilterExceptions.
			// edgeFilterExceptions uses filter.id as the key while filterExceptions uses columnId as the key.
			// TODO Need to review columnId vs id then we follow the same rule for local filters.
			return 	exceptions[filterEntry.columnId] || exceptions[filterEntry.id] ||
					(applyToCompoundFilters && filterEntry.isCompound() && filterEntry.compoundFilterMatches(exceptions));
		},

		/**
		 * In the case where the same item is included  as both an edge filter and a dataPoint filter,
		 *	Eg: Year=2004 with (Year=2005, country=Canada) should yield all the countries with 2004 and an extra point for 2005,Canada
		 *		....we need a modified filter for query generation that combines the simple filter as a term of a compound one.
		 *
		 * In this case, we want to
		 * 1) keep the filters as 2 distinct entities for reporting purposes
		 * 2) combine the edge filters against those column(s) with the dataPoint filter as an additional 'or' term from the query perspective.
		 *
		 * To do this, we create a temporary filter collection and populate it with the combined result.
		 * NOTE: Merged filters are ONLY FOR QUERY PURPOSES.  The filter collections are not changed
		 * (other than a temporary instance used for query generation).
		 *
		 * @param filterCollection - the filterCollection to be processed
		 * @returns EITHER...the original filterCollection (if the collection has no merging)
		 *			OR...a temporary collection with include type datapoint and edge filters on the same columns merged.
		 */
		_mergeEdgeAndPointFiltersOnSameColumns: function(filterCollection, options) {
			var mergeInfo=this._prepareDataToMergeEdgeAndPointFiltersIfNeeded(filterCollection, options);
			if (!mergeInfo) {
				return filterCollection;	//No merging is necessary, return the original collection.
			}

			//There is an overlap of filters on at least one ColumnId for an edge and a point.
			var tempFilterCollection = new FilterCollection();

			filterCollection.each(function(filterEntry) {
				if (filterEntry.isCompound()) {
					var finalCompoundFilter=this._createFinalCompoundFilter(filterCollection, filterEntry, mergeInfo);
					tempFilterCollection.add(finalCompoundFilter, { silent: true });
				}
			}.bind(this));

			//Make sure any filters that were NOT combined will be maintained as simple filters.
			//Note: ordinal filters (ranges) will never be combined so always need to be added to the target.
			filterCollection.each(function(filterEntry) {
				if (filterEntry.isOrdinalFilter() ||
						(!filterEntry.isCompound() && mergeInfo.unprocessedSimpleColumnIdMap[filterEntry.columnId])) {
					var targetFilterEntry=$.extend(true, {}, filterEntry);
					tempFilterCollection.add(targetFilterEntry, { silent: true });
				}
			}.bind(this));
			return tempFilterCollection;
		},

		/**
		 * 'finalize' a datapointFilter (and, optionally, any overlapping edge filters) into a new compound filter.
		 * A finalized compound filter may simply be a copy of the original dataPoint filter
		 * OR, if edge filters on the same columns exist, the edge filters may be merged into the filter as a new 'or' term.
		 * Note: this only applies to include-type dataPoint filters.
		 * @param filterCollection - the source filter collection
		 * @param dataPointFilter - the source dataPointFilter to be copied for addition to the new collection
		 * @param mergeInfo - information needed to decide whether combining is necessary.
		 * @returns a (possibly modified) copy of the original dataPoint filter to be added to the target collection.
		 */
		_createFinalCompoundFilter: function(filterCollection, dataPointFilter, mergeInfo) {
			var finalCompoundFilter=new FilterEntry(dataPointFilter.toJSON());
			finalCompoundFilter._compoundFilterFromJSON();
			//Collect all of the columnID's for a particular compound filter
			//We're going to add the compound filter either 'as is' or with a new term (that is the overlap simple filter).
			if (dataPointFilter.operator==='or') {
				//For each of the columnIds in the compound filter, lookup a simple filter on the original collection
				//if it exists.....merge the simple filter into the compound filter as an additional term of the 'or'.
				_.each(mergeInfo.compoundFilterColumnIds, function(columnId) {
					var catFilterKey=filterCollection.getCategoryFilterKey(columnId);
					var simpleFilterForThisCategory=filterCollection.get(catFilterKey);
					if (simpleFilterForThisCategory) {
						mergeInfo.unprocessedSimpleColumnIdMap[columnId]=undefined;
						var copyOfSimpleFilterTermToAdd=$.extend(true, {}, simpleFilterForThisCategory);
						finalCompoundFilter.values.add(copyOfSimpleFilterTermToAdd, { silent: true });
					}
				}.bind(this));
			}
			return finalCompoundFilter;
		},

		/**
		 * Return information needed to merge edge and datapoint filters (if combining is necessary.....return null if not necessary).
		 * Merging means taking simple edge filter on a given ID and adding it as a new 'or term' on a datapoint filter (for query only)
		 * @param filterCollection - the filterCollection to check.
		 * @param options - options which may include edgeFilterExceptions.
		 * @returns data needed for combining edge and datapoint filters IF combining is necessary (null if combining is not needed).
		 *			(mostly, the data needed is a map of all unprocessed simple filter columnIds).
		 */
		_prepareDataToMergeEdgeAndPointFiltersIfNeeded: function(filterCollection, options) {
			//Case 1 where merge is unnecessary: there are no compound filters.
			var compoundFilterColumnIds=filterCollection.getSelectedCompoundFilterColumnIds();
			if (compoundFilterColumnIds.length===0) {
				return null;
			}
			//Case 2 where merge is unnecessary: there are no simple filters
			var simpleColumnIds=filterCollection.getSelectedSimpleFilterColumnIds();
			if (simpleColumnIds.length===0) {
				return null;
			}
			//Case 3 where merge is unnecessary: there are no overlapping columnIds in compound and simple filters.
			var overlappingColumnIds=_.intersection(simpleColumnIds, compoundFilterColumnIds);
			if (overlappingColumnIds.length===0) {
				return null;
			}
			//Case 4 where merge is unnecessary: the overlapping columnIds are all contained in the edgeFilterExceptions list.
			if (options.edgeFilterExceptions && _.difference(overlappingColumnIds, options.edgeFilterExceptions).length===0) {
				return null;
			}

			//Merge IS necessary....
			var unprocessedSimpleColumnIdMap={};
			_.each(simpleColumnIds, function(simpleColumnId) {
				unprocessedSimpleColumnIdMap[simpleColumnId]=simpleColumnId;
			}.bind(this));

			return {
				simpleColumnIds: simpleColumnIds,
				compoundFilterColumnIds: compoundFilterColumnIds,
				unprocessedSimpleColumnIdMap: unprocessedSimpleColumnIdMap
			};
		},

		/**
		 * look up a dataItem that corresponds to this filterEntry (shares its itemId and aggregationType).
		 * For ranges, only a dataItem that is projected in this layer/view should be returned as an associated data item.
		 * If there's a match, set the filter against that item not against the column.
		 * @param filterEntry - the filter entry (could be categorical or range)
		 * @param layerId - the layerId being processed.	//TODO: change to viewId.
		 */
		_getAssociatedDataItem: function(filterEntry, layerId) {
			var associatedDataItem = null;
			if (!filterEntry.filterBins && filterEntry.isRangeOperator() && this._dataItemsMap[filterEntry.id]) {
				associatedDataItem = _.find(this._dataItemsMap[filterEntry.id], function(visibleItemInfo) {
					// Make sure the aggregation type matches for range filter.
					return visibleItemInfo.layerId === layerId && visibleItemInfo.dataItemAPI.getAggregationType() === filterEntry.aggregationType;
				});
			} else if (filterEntry.filterBins) {
				associatedDataItem = _.find(this._dataItemsMap[filterEntry.columnId], function(visibleItemInfo) {
					return visibleItemInfo.dataItemAPI.isBinned() && visibleItemInfo.layerId === layerId && filterEntry.id.indexOf(visibleItemInfo.dataItemAPI.getUniqueId()) !== -1;
				});
			}
			return (associatedDataItem && associatedDataItem.dataItemAPI) || null;
		},

		/**
		 * FILTERAPI:
		 * Present the filters for this query as a simple array form needed by the query engine
		 * in the 'querySpec' url parameter.
		 * [{ column: columnindex, operator: {eq|in}, { value: value | values: [values...]},
		 *  { column: columnindex, operator: {eq|in}, { value: value | values: [values...]}
		 *  ...
		 * ]
		 * @param options - an object that customizes the behaviour of toSpec with the following members.
		 *		.edgeFilterExceptions: similar to filterExceptions except only edge filters are considered.
		 *			This is for localFilters against OLAP where an edge filter has been converted to a setSelection against a dataItem.
		 *			RULE: exclude any converted edge filters but process dataPoint filters normally (as filters).
		 *		.filterExceptions: a list of columnId's that, if matched, will not be added to the query
		 *			This is the options used by the old filters collection AND could potentially be used for the pageContext 'brushing case'
		 *			RULE: any visible edge items or matching points are excluded from filtering and hilighted
		 *			TODO: investigate either adapting pageContext to generate filters using queryFilterSpec or remove filterExceptions.
		 * @returns the array as shown above.
		 */
		_toSpec: function(filterCollection, options) {
			var filterExceptionsMap = this._prepareFilterExceptionsMap(options);

			// Convert filters to the format used by the query spec
			var filterSpec = [];
			filterCollection.each(function(filterEntry) {
				if (!this._isFilterException(filterEntry, filterExceptionsMap, /*applyToCompoundFilters*/!!(options && options.filterExceptions), options)) {
					var filterEntrySpec = filterEntry.toQuerySpec({
						associatedDataItemAPI: this._getAssociatedDataItem(filterEntry, options.layerId),
						shouldApplyIsNullValueExpression: options.shouldApplyIsNullValueExpression,
						addExtraQueryDataItem: options && options.addExtraQueryDataItem
					});
					if (!_.isEmpty(filterEntrySpec.expression)) {
						if (options && options.dsf) {
							filterEntrySpec.dsf = options.dsf;  // add identifying attribute to filter so server can tell
						}
						filterSpec.push(filterEntrySpec);
					}
				}
			}.bind(this));
			return filterSpec;
		},

		/**
		 * if filterExceptions have been specified, convert the list to a map for fast lookup.
		 * Any column in the filterExceptionsMap will not be included in the query.
		 * @param filterExceptionsOption - a list of exceptions
		 * (filterExceptions are mostly specified for visible query fields on widget-to-widget filters so that data is hilighted not filtered).
		 */
		_prepareFilterExceptionsMap: function(options) {
			var filterExceptionsOption = (options && (options.filterExceptions || options.edgeFilterExceptions)) || [];

			var exceptionsMap = {};
			_.each(filterExceptionsOption, function(index) {
				exceptionsMap[index] = [];
			});
			return exceptionsMap;
		}
	});

	return QueryFilterSpec;
});
